import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  CadArtifactRef,
  MouldingGraph,
  MouldingFlowSummary,
  MouldingItem,
  MouldingNodeRun,
  MouldingStepDefinitions,
} from '../../../models/moulding-item.model';
import { PullDirection } from '../../../models/pull-direction.model';

/**
 * Root-scoped HTTP client for Moulding flow resources and processing
 * endpoints. It maps API requests and responses only; page-specific graph
 * state and processing orchestration belong to MouldingProcessingService.
 */
@Injectable({
  providedIn: 'root',
})
export class MouldingItemService {
  private http = inject(HttpClient);

  getItems$(): Observable<MouldingFlowSummary[]> {
    return this.http.get<MouldingFlowSummary[]>('/moulding-api/flows/');
  }

  getItem$(id: number): Observable<MouldingItem> {
    return this.http.get<MouldingItem>(`/moulding-api/flow/${id}/`);
  }

  getNodeRun$(flowId: number, nodeId: string): Observable<MouldingNodeRun> {
    return this.http.get<MouldingNodeRun>(
      `/moulding-api/flow/${flowId}/node/${nodeId}/`,
    );
  }

  getStepDefinitions$(): Observable<MouldingStepDefinitions> {
    return this.http.get<MouldingStepDefinitions>(
      '/moulding-api/step-definitions/',
    );
  }

  createItem$(): Observable<MouldingItem> {
    return this.http.post<MouldingItem>('/moulding-api/flows/', {});
  }

  deleteItem$(id: number): Observable<void> {
    return this.http.delete<void>(`/moulding-api/flow/${id}/`);
  }

  saveGraph$(id: number, graph: MouldingGraph): Observable<MouldingItem> {
    return this.http.patch<MouldingItem>(`/moulding-api/flow/${id}/`, {
      graph,
    });
  }

  updateItem$(
    id: number,
    changes: Partial<Pick<MouldingItem, 'name' | 'run_downstream_enabled'>>,
  ): Observable<MouldingItem> {
    return this.http.patch<MouldingItem>(`/moulding-api/flow/${id}/`, changes);
  }

  /**
   * Creates or restarts a node run. Processing runs continue asynchronously
   * over the flow's WebSocket; CAD input runs complete in the response.
   */
  startNodeStep$(
    flowId: number,
    nodeId: string,
    graph: MouldingGraph,
    cadInputs?: Record<string, CadArtifactRef>,
    runDownstream = false,
  ): Observable<MouldingNodeRun> {
    return this.http.post<MouldingNodeRun>(
      `/moulding-api/flow/${flowId}/node/${nodeId}/start-step/`,
      {
        graph,
        ...(cadInputs ? { cad_inputs: cadInputs } : {}),
        ...(runDownstream ? { run_downstream: true } : {}),
      },
    );
  }

  setPullDirection$(
    flowId: number,
    nodeId: string,
    pullDirection: PullDirection,
    graph?: MouldingGraph,
    runDownstream = false,
  ): Observable<MouldingNodeRun> {
    const body: {
      pull_direction: PullDirection;
      graph?: MouldingGraph;
      run_downstream?: true;
    } = { pull_direction: pullDirection };
    if (graph) {
      body.graph = graph;
    }
    if (runDownstream) {
      body.run_downstream = true;
    }

    return this.http.patch<MouldingNodeRun>(
      `/moulding-api/flow/${flowId}/node/${nodeId}/pull-direction/`,
      body,
    );
  }
}
